import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Story } from '@/utils/storiesData';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import StoryCard from '@/components/story/StoryCard';
import HorizontalScroller from '@/components/mobile/HorizontalScroller';
interface LatestStoriesSectionProps {
  stories: Story[];
}
const LatestStoriesSection: React.FC<LatestStoriesSectionProps> = ({
  stories
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    resolvedTheme
  } = useTheme();
  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';

  // Get diverse stories from different categories
  const diverseStories = React.useMemo(() => {
    if (!stories || stories.length === 0) return [];

    // Get unique categories
    const categories = Array.from(new Set(stories.map(story => story.category)));

    // Create an array to hold our diverse story selection
    const result: Story[] = [];

    // Add one story from each category until we have 8 or run out of categories
    for (const category of categories) {
      if (result.length >= 8) break;
      const storyFromCategory = stories.find(story => story.category === category && !result.includes(story));
      if (storyFromCategory) {
        result.push(storyFromCategory);
      }
    }

    // If we don't have enough stories yet, fill with latest from any category
    if (result.length < 8) {
      const remainingStories = stories.filter(story => !result.includes(story)).sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()).slice(0, 8 - result.length);
      result.push(...remainingStories);
    }
    return result;
  }, [stories]);
  return <div className="py-[54px]">
      <div className="max-w-screen-2xl mx-auto pl-4 pr-0 md:px-8 lg:px-12 xl:px-16">
        <div className="flex items-center justify-between mb-4">
          <h2 className={cn("text-2xl font-chroma", textColor)}>Explore Stories</h2>
          <Button variant="link" onClick={() => navigate('/browse')} className={textColor}>
            View All <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        
        <div className={`${isMobile ? 'hidden' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-6 lg:gap-8'}`}>
          {diverseStories.map(story => <StoryCard key={story.id} story={story} featured={false} aspectRatio="3/2" hideAuthor={true} />)}
        </div>
        
        {isMobile && <div className="pl-0 px-0">
            <HorizontalScroller noLeftPadding showNextCard={false} snap>
              {diverseStories.map(story => <StoryCard key={story.id} story={story} featured={false} aspectRatio="3/2" hideAuthor={true} />)}
            </HorizontalScroller>
          </div>}
      </div>
    </div>;
};
export default LatestStoriesSection;